import styled from "@emotion/styled";
import { Box, CircularProgress, useTheme } from "@mui/material";
import BaseTypography from "@ui/atoms/BaseTypography/BaseTypography";
import * as React from "react";
import Logo from "@ui/atoms/Logo/Logo";

const CircularProgressContainer = styled(Box)({
  display: "inline-flex",
  position: "relative"
});

const CircularProgressContentBox = styled(Box)({
  alignItems: "center",
  bottom: 0,
  display: "flex",
  justifyContent: "center",
  left: 0,
  position: "absolute",
  right: 0,
  top: 0
});

export enum CircularProgressWithContentVariant {
  Default = "default",
  Branded = "branded"
}

const GRADIENT_END_COLOR = "var(--mui-palette-primary-100)";

// Designer-supplied gradient start offset.
const GRADIENT_START_OFFSET = "24.69%";

export interface CircularProgressWithContentProps {
  /**
   * Accessible name announced for the progress ring. Screen readers say nothing
   * for a bare MUI CircularProgress, so this defaults to a generic "Loading" —
   * pass something specific ("Uploading file") where the caller knows better.
   */
  label?: string;
  value?: number;
  maxValue?: number;
  iconSize?: number;
  brandedProgressSize?: number;
  brandedProgressThickness?: number;
  variant?: CircularProgressWithContentVariant;
}

/**
 * Rounds and clamps a progress value to [0, maxValue]. Returns undefined for a
 * non-finite/absent value (caller renders an indeterminate ring). Pure — tested.
 */
export const clampProgress = (
  value: number | undefined,
  maxValue?: number
): number | undefined => {
  if (value === undefined || !Number.isFinite(value)) return undefined;
  return Math.max(0, Math.min(Math.round(value), maxValue ?? 100));
};

export const CircularProgressWithContent = ({
  brandedProgressSize = 70,
  brandedProgressThickness = 4,
  iconSize = 28,
  label = "Loading",
  maxValue,
  value,
  variant = CircularProgressWithContentVariant.Default
}: CircularProgressWithContentProps) => {
  const theme = useTheme();
  const gradientId = React.useId();

  const progressValue = clampProgress(value, maxValue);

  const variantStyles = {
    [CircularProgressWithContentVariant.Branded]: {
      size: brandedProgressSize,
      thickness: brandedProgressThickness
    },
    [CircularProgressWithContentVariant.Default]: {
      size: undefined,
      thickness: 3.6
    }
  };

  return (
    <CircularProgressContainer>
      <svg width={0} height={0} style={{ position: "absolute" }}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop
              offset={GRADIENT_START_OFFSET}
              stopColor={theme.vars.palette.primary.main}
            />
            <stop offset="100%" stopColor={GRADIENT_END_COLOR} />
          </linearGradient>
        </defs>
      </svg>

      <CircularProgress
        aria-label={label}
        variant={progressValue !== undefined ? "determinate" : "indeterminate"}
        value={progressValue ?? undefined}
        size={variantStyles[variant].size}
        thickness={variantStyles[variant].thickness}
        sx={{
          "& .MuiCircularProgress-circle": {
            stroke: `url(#${gradientId})`,
            strokeLinecap: "round"
          },
          "& .MuiCircularProgress-circleIndeterminate": {
            strokeDasharray: "80 200",
            strokeDashoffset: 0
          }
        }}
      />
      <CircularProgressContentBox>
        {progressValue !== undefined ? (
          <BaseTypography
            data-test-id="circular-progress-value"
            component="div"
            variant="caption"
            sx={{ color: theme.vars.palette.grey["600"], paddingTop: "3px" }}
            text={`${progressValue}%`}
          />
        ) : variant === CircularProgressWithContentVariant.Branded ? (
          <Logo
            variant="mark"
            width={iconSize}
            startColor={theme.vars.ui.logo.gradient.from}
            endColor={theme.vars.ui.logo.gradient.to}
          />
        ) : null}
      </CircularProgressContentBox>
    </CircularProgressContainer>
  );
};
